if (!customElements.get('video-lightbox')) {
  // Remembers in this browser that the visitor agreed to load YouTube videos.
  const YOUTUBE_CONFIRMED_KEY = 'hjh:video-lightbox:youtube-confirmed';
  // Lightbox to reopen after the reload that makes a new consent effective.
  const REOPEN_KEY = 'hjh:video-lightbox:reopen';
  // Placeholder that Consentmo puts in place of a YouTube iframe it blocks.
  const CONSENTMO_PLACEHOLDER_SELECTOR = '.csm-youtube-placeholder';

  const readStorage = (storage, key) => {
    try {
      return window[storage].getItem(key);
    } catch {
      return null;
    }
  };

  const writeStorage = (storage, key, value) => {
    try {
      if (value === null) {
        window[storage].removeItem(key);
      } else {
        window[storage].setItem(key, value);
      }
    } catch {
      // Storage unavailable (e.g. private mode): the notice simply shows again.
    }
  };

  class VideoLightbox extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('modal-dialog');
      this.player = this.querySelector('[data-video-lightbox-player]');
      this.consent = this.querySelector('[data-video-lightbox-consent]');
      this.marketingHint = this.querySelector('[data-video-lightbox-marketing-hint]');
      this.settingsButton = this.querySelector('[data-video-lightbox-settings]');
      this.loadButton = this.querySelector('[data-video-lightbox-load]');
      this.youtubeId = this.dataset.youtubeId;
      this.confirmYoutube = this.dataset.confirmYoutube !== 'false';

      if (
        !this.modal
        || !this.player
        || !this.consent
        || !this.marketingHint
        || !this.settingsButton
        || !this.loadButton
        || !this.youtubeId
      ) return;

      // Set while the Consentmo settings are open from this lightbox.
      this.awaitingConsent = false;
      // Set after the consent reload, so a still blocked player cannot reload again.
      this.reloadedForConsent = false;

      // Delegated, so openers rendered before or after the lightbox both work.
      this.boundDocumentClick = this.handleDocumentClick.bind(this);
      document.addEventListener('click', this.boundDocumentClick);

      this.boundOpenSettings = this.openConsentSettings.bind(this);
      this.settingsButton.addEventListener('click', this.boundOpenSettings);

      this.boundConfirmAndLoad = this.confirmAndLoad.bind(this);
      this.loadButton.addEventListener('click', this.boundConfirmAndLoad);

      // Shopify's consent event plus Consentmo's Consentmo.events.ConsentUpdate,
      // named literally because Consentmo may load after this element. They may
      // be dispatched on window or document; duplicates are harmless.
      this.boundConsentChange = this.handleConsentChange.bind(this);
      this.consentEvents = ['visitorConsentCollected', 'consentmoSignal'];
      this.consentEvents.forEach((name) => {
        window.addEventListener(name, this.boundConsentChange);
        document.addEventListener(name, this.boundConsentChange);
      });

      this.boundDialogClosed = this.handleConsentDialogClosed.bind(this);
      window.addEventListener('consentmo:dialogClosed', this.boundDialogClosed);
      document.addEventListener('consentmo:dialogClosed', this.boundDialogClosed);

      // Detects Consentmo replacing the iframe with its placeholder.
      this.playerObserver = new MutationObserver(this.handlePlayerChange.bind(this));
      this.playerObserver.observe(this.player, { childList: true, subtree: true });

      // Removing the iframe on close stops playback reliably.
      this.modalObserver = new MutationObserver(() => {
        if (this.modal.hasAttribute('open')) return;
        this.player.replaceChildren();
        this.consent.hidden = true;
      });
      this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });

      if (readStorage('sessionStorage', REOPEN_KEY) === this.modal.id) {
        writeStorage('sessionStorage', REOPEN_KEY, null);
        this.reloadedForConsent = true;
        VideoLightbox.whenConsentReady(() => this.open(this.findOpener()));
      }
    }

    disconnectedCallback() {
      if (this.boundDocumentClick) document.removeEventListener('click', this.boundDocumentClick);
      if (this.boundOpenSettings) this.settingsButton.removeEventListener('click', this.boundOpenSettings);
      if (this.boundConfirmAndLoad) this.loadButton.removeEventListener('click', this.boundConfirmAndLoad);
      if (this.boundConsentChange) {
        this.consentEvents.forEach((name) => {
          window.removeEventListener(name, this.boundConsentChange);
          document.removeEventListener(name, this.boundConsentChange);
        });
      }
      if (this.boundDialogClosed) {
        window.removeEventListener('consentmo:dialogClosed', this.boundDialogClosed);
        document.removeEventListener('consentmo:dialogClosed', this.boundDialogClosed);
      }
      if (this.playerObserver) this.playerObserver.disconnect();
      if (this.modalObserver) this.modalObserver.disconnect();
    }

    /**
     * Whether the visitor allowed marketing in the cookie banner. Consentmo's own
     * state comes first: it updates at once, Shopify's only after a reload.
     * YouTube is treated as marketing; without a consent API nothing is allowed.
     * @returns {boolean}
     */
    static marketingAllowed() {
      if (typeof window.Consentmo?.marketingAllowed === 'function') {
        return Boolean(window.Consentmo.marketingAllowed());
      }

      const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;

      return Boolean(
        customerPrivacy
        && typeof customerPrivacy.marketingAllowed === 'function'
        && customerPrivacy.marketingAllowed()
      );
    }

    /**
     * Whether the Consentmo settings dialog can be opened.
     * @returns {boolean}
     */
    static consentSettingsAvailable() {
      return typeof window.Consentmo?.openDialog === 'function';
    }

    /**
     * Runs the callback once a consent API is loaded, or after 5 seconds.
     * @param {Function} callback - Function to run.
     */
    static whenConsentReady(callback) {
      let checks = 0;
      const interval = window.setInterval(() => {
        checks += 1;
        const ready = typeof window.Consentmo?.marketingAllowed === 'function'
          || typeof window.Shopify?.customerPrivacy?.marketingAllowed === 'function';

        if (ready || checks >= 50) {
          window.clearInterval(interval);
          callback();
        }
      }, 100);
    }

    /**
     * Finds the first button that opens this lightbox.
     * @returns {?Element}
     */
    findOpener() {
      return document.querySelector(`[data-video-lightbox-open][aria-controls="${this.modal.id}"]`);
    }

    /**
     * Opens the lightbox when one of its openers is clicked.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      const opener = evt.target.closest(`[data-video-lightbox-open][aria-controls="${this.modal.id}"]`);
      if (!opener) return;

      this.open(opener);
    }

    /**
     * Opens the modal with the player, or with the YouTube notice while consent
     * is missing. The player is created after opening so it is not paused by
     * the modal's pauseAllMedia() call.
     * @param {?Element} opener - Button that opened the lightbox.
     */
    open(opener) {
      this.opener = opener;
      this.modal.open(opener);

      if (!VideoLightbox.marketingAllowed()) {
        // Consentmo blocks YouTube without marketing consent, so with Consentmo
        // the way to the video leads through its settings dialog. Without it,
        // the click on "Load video" is the consent.
        this.showNotice(VideoLightbox.consentSettingsAvailable() ? 'settings' : 'load');
      } else if (this.confirmYoutube && !readStorage('localStorage', YOUTUBE_CONFIRMED_KEY)) {
        this.showNotice('load');
      } else {
        this.loadPlayer();
      }
    }

    /**
     * Shows the YouTube notice with the button for the next step.
     * @param {string} step - 'settings' (open the cookie settings) or 'load'.
     */
    showNotice(step) {
      const withSettings = step === 'settings';

      this.player.replaceChildren();
      this.marketingHint.hidden = !withSettings;
      this.settingsButton.hidden = !withSettings;
      this.loadButton.hidden = withSettings;
      this.consent.hidden = false;
      (withSettings ? this.settingsButton : this.loadButton).focus();
    }

    /**
     * Remembers the YouTube confirmation and loads the player.
     */
    confirmAndLoad() {
      writeStorage('localStorage', YOUTUBE_CONFIRMED_KEY, '1');
      this.loadPlayer();
    }

    /**
     * Closes the lightbox (its focus trap would block the dialog) and opens the
     * Consentmo settings. Once marketing is allowed there, the page reloads and
     * reopens the lightbox.
     */
    openConsentSettings() {
      this.awaitingConsent = true;
      this.modal.close();
      window.Consentmo.openDialog();
    }

    /**
     * Reloads the page after marketing was allowed in the settings opened from
     * this lightbox. The visitor saw the YouTube notice first, so this also
     * counts as the YouTube confirmation.
     */
    handleConsentChange() {
      if (!this.awaitingConsent) return;

      // The consent state is updated after the event, see hjh-trustpilot-script.
      setTimeout(() => {
        if (!this.awaitingConsent || !VideoLightbox.marketingAllowed()) return;

        this.awaitingConsent = false;
        writeStorage('localStorage', YOUTUBE_CONFIRMED_KEY, '1');
        this.reloadAndReopen();
      }, 0);
    }

    /**
     * Stops waiting once the settings are closed without marketing consent, so
     * a later consent elsewhere does not reload the page. The delay lets a
     * consent update that follows the close event arrive first.
     */
    handleConsentDialogClosed() {
      if (!this.awaitingConsent) return;

      setTimeout(() => {
        if (this.awaitingConsent && !VideoLightbox.marketingAllowed()) this.awaitingConsent = false;
      }, 1000);
    }

    /**
     * Handles Consentmo blocking the player. With marketing consent that was
     * given during this page view, a reload makes it effective; otherwise the
     * notice shows again.
     */
    handlePlayerChange() {
      if (!this.player.querySelector(CONSENTMO_PLACEHOLDER_SELECTOR)) return;

      if (VideoLightbox.marketingAllowed() && !this.reloadedForConsent) {
        this.reloadAndReopen();
        return;
      }

      this.showNotice(VideoLightbox.consentSettingsAvailable() ? 'settings' : 'load');
    }

    /**
     * Reloads the page and reopens this lightbox afterwards.
     */
    reloadAndReopen() {
      writeStorage('sessionStorage', REOPEN_KEY, this.modal.id);
      window.location.reload();
    }

    /**
     * Creates the YouTube player and starts playback.
     */
    loadPlayer() {
      const params = new URLSearchParams({ autoplay: '1', rel: '0', playsinline: '1' });
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.youtubeId)}?${params}`;
      iframe.title = this.dataset.title || '';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';

      this.consent.hidden = true;
      this.player.replaceChildren(iframe);
      this.modal.focus();
    }
  }

  customElements.define('video-lightbox', VideoLightbox);
}
